import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { WhatsAppButton } from '@/components/whatsapp'
import type { Patient } from '@/lib/api-client'
import { formatCurrency, maskNRIC } from '@/lib/utils'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowUpDown, Eye, FileText, Maximize2, MoreHorizontal } from 'lucide-react'

// Branch colors matching dashboard
const BRANCH_COLORS: Record<string, string> = {
  'Shah Alam': '#f59e0b',
  Putrajaya: '#4a8fe8',
  Bangi: '#10b981',
  'Kuala Lumpur': '#a855f7',
  KL: '#a855f7',
}

/** Columns hidden by default (still available in the Columns toggle) */
export const PATIENT_DEFAULT_HIDDEN_COLUMNS = { pno: false, nric: false, branchName: false }

function formatShortDate(date: string) {
  return new Date(date).toLocaleDateString('en-MY', { day: '2-digit', month: 'short' })
}

const WA_STATUS_STYLES: Record<string, string> = {
  replied: 'bg-success/15 text-success',
  read: 'bg-info/15 text-info',
  delivered: 'bg-muted text-muted-foreground',
  sent: 'bg-muted text-muted-foreground',
  pending: 'bg-muted text-muted-foreground',
  failed: 'bg-destructive/15 text-destructive',
  'opted out': 'bg-warning/15 text-warning',
}

function WaStatusTag({ label }: { label: string }) {
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        WA_STATUS_STYLES[label] ?? 'bg-muted text-muted-foreground'
      }`}
    >
      {label}
    </span>
  )
}

/** Two-line WhatsApp campaign history cell */
function WaHistoryCell({ patient }: { patient: Patient }) {
  const blasts = patient.waBlastCount ?? 0
  if (blasts === 0) {
    return (
      <div className="flex flex-col gap-0.5 leading-tight">
        <span className="inline-flex w-fit items-center rounded border border-dashed border-border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          never blasted
        </span>
        {patient.waOptedOut && <WaStatusTag label="opted out" />}
      </div>
    )
  }
  const status = patient.waOptedOut
    ? 'opted out'
    : patient.waLastReplied
      ? 'replied'
      : patient.waLastStatus === 'read'
        ? 'read'
        : (patient.waLastStatus ?? 'sent')
  const statusLabel = status === 'read' ? 'read, no reply' : status
  return (
    <div className="flex flex-col gap-0.5 leading-tight">
      <div className="flex items-center gap-1.5 font-medium">
        <span>
          {blasts} {blasts === 1 ? 'blast' : 'blasts'}
        </span>
        <WaStatusTag label={statusLabel === 'read, no reply' ? 'read' : statusLabel} />
        {statusLabel === 'read, no reply' && (
          <span className="text-[11px] text-muted-foreground">no reply</span>
        )}
      </div>
      <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">
        {patient.waLastSentAt ? `${formatShortDate(patient.waLastSentAt)} · ` : ''}
        {patient.waLastCampaignName ?? 'Campaign'}
        {patient.waEverReplied && !patient.waLastReplied ? ' · replied before' : ''}
      </div>
    </div>
  )
}

interface PatientColumnsProps {
  onView: (patient: Patient) => void
  onEdit: (patient: Patient) => void
}

export function getPatientColumns({ onView, onEdit }: PatientColumnsProps): ColumnDef<Patient>[] {
  return [
    // Selection checkbox
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },

    // PNO
    {
      accessorKey: 'pno',
      header: 'PNO',
      cell: ({ row }) => <span className="font-medium">{row.getValue('pno')}</span>,
    },

    // Name
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row, table }) => {
        const patient = row.original
        const color = patient.branchName
          ? BRANCH_COLORS[patient.branchName] || '#6b7280'
          : '#6b7280'
        const branchHidden = !table.getColumn('branchName')?.getIsVisible()
        const pnoHidden = !table.getColumn('pno')?.getIsVisible()
        return (
          <div className="leading-tight">
            <div className="flex items-center gap-2 font-medium">
              {branchHidden && patient.branchName && (
                <span
                  className="inline-block h-[7px] w-[7px] flex-shrink-0 rounded-full"
                  style={{ background: color }}
                  title={patient.branchName}
                  aria-label={patient.branchName}
                />
              )}
              <span>{patient.name}</span>
            </div>
            {pnoHidden && (
              <div className="font-mono text-[11px] text-muted-foreground">{patient.pno}</div>
            )}
          </div>
        )
      },
    },

    // NRIC (masked)
    {
      accessorKey: 'nric',
      header: 'NRIC',
      cell: ({ row }) => {
        const nric = row.getValue('nric') as string | null
        return nric ? (
          <span>{maskNRIC(nric)}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      },
    },

    // Phone
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ row }) => {
        const phone = row.getValue('phone') as string | null
        return phone ? <span>{phone}</span> : <span className="text-muted-foreground">-</span>
      },
    },

    // Branch with colored badge
    {
      accessorKey: 'branchName',
      header: 'Branch',
      cell: ({ row }) => {
        const branch = row.getValue('branchName') as string | null
        const color = branch ? BRANCH_COLORS[branch] || '#6b7280' : '#6b7280'
        return branch ? (
          <Badge style={{ background: color }} className="text-white">
            {branch}
          </Badge>
        ) : null
      },
    },

    // Visits
    {
      accessorKey: 'totalVisits',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Visits
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const visits = row.getValue('totalVisits') as number | null
        return <div className="text-center font-medium">{visits ?? 0}</div>
      },
    },

    // Total Spent
    {
      accessorKey: 'totalSpent',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Spent
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const spent = row.getValue('totalSpent') as string | null
        const amount = Number.parseFloat(spent || '0')
        return <div className="text-right font-medium">{formatCurrency(amount)}</div>
      },
    },

    // Last Visit
    {
      accessorKey: 'lastVisitDate',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Last Visit
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const date = row.getValue('lastVisitDate') as string | null
        if (!date) return <span className="text-muted-foreground">-</span>

        const formatted = new Date(date).toLocaleDateString('en-MY', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
        return <div>{formatted}</div>
      },
    },

    // WhatsApp campaign history
    {
      id: 'waHistory',
      accessorFn: (row) => row.waLastSentAt ?? null,
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() !== 'desc')}
        >
          WhatsApp
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <WaHistoryCell patient={row.original} />,
    },

    // WhatsApp Button
    {
      id: 'whatsapp',
      header: '',
      enableHiding: false,
      cell: ({ row }) => {
        const patient = row.original
        return (
          <WhatsAppButton
            phone={patient.phone}
            patientName={patient.name}
            branchName={patient.branchName}
            compact
          />
        )
      },
    },

    // Actions
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const patient = row.original

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-9 w-9 p-0" onClick={(e) => e.stopPropagation()}>
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-[13px]">Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onView(patient)} className="text-[13px]">
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(patient)} className="text-[13px]">
                <Maximize2 className="mr-2 h-4 w-4" />
                View Full Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onView(patient)} className="text-[13px]">
                <FileText className="mr-2 h-4 w-4" />
                View Invoices
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]
}
